import {
  PlaycastMessage,
  type PlaycastMessageProcessMacroStep,
} from '@playcastdotio/Messaging';
import type { MacroStepTarget } from './interfaces/IPlayjectorService';

/**
 * Build a processMacroStepMSG IPC envelope.
 *
 * `processMacroStepMSG` does not yet have a Zod validator in `libs/Messaging`
 * (the wire schema authority is `apps/playjector/src/PlayJectorLib/source/
 * SchemaList.cpp::processMacroStepMSG`, consumed by
 * `ProcessDirectory::onMacroStep`), so we construct the envelope manually.
 * The TS shape, however, IS now in `libs/Messaging/src/types/messages/
 * process.ts` as `PlaycastMessageProcessMacroStep`, so the envelope is fully
 * typed end-to-end and host-electron's `messageHandler.ts` can narrow on
 * `header.action === 'macroStep'`.
 *
 * The optional `screenshotName` controls the filename Playjector uses when
 * saving the captured screenshot to disk (defaults to "macro_latest" in C++).
 *
 * The optional `target` carries the per-instance routing identity
 * (production-readiness Phase 5, R13 / macro G1): when present, `streamId` +
 * `gameInstanceIndex` ride in the message body and the C++ side resolves the
 * capture to that instance's monitor (and echoes the identity on the reply).
 * Absent = legacy whole-machine aggregate behavior.
 */
export function buildProcessMacroStepMessage(
  variants: PlaycastMessageProcessMacroStep['message']['variants'],
  screenshotName?: string,
  target?: MacroStepTarget,
): PlaycastMessage<PlaycastMessageProcessMacroStep> {
  return {
    signature: 'playcast',
    header: {
      tag: `${Date.now()}.macro-step`,
      source: 'host',
      schemaVersion: 5,
      timestamp: Date.now(),
      user: { id: 'host', auth: 'host' },
      target: 'process',
      action: 'macroStep',
      isReply: false,
    },
    body: {
      message: {
        variants,
        ...(screenshotName ? { screenshotName } : {}),
        ...(target
          ? { streamId: target.streamId, gameInstanceIndex: target.gameInstanceIndex }
          : {}),
      },
      timings: [],
      stats: [],
      echo: false,
    },
  };
}
